import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';

import {
  MenuList,
  MaterialIcon,
  MenuListItem,
  HOCs,
} from '@pearsontechnology/aero-components';

import { intl } from 'containers/LanguageProvider/IntlGlobalProvider';
import BaseMoreButton from 'components/MoreButton';
import { ActionTypes } from 'constants/enums';
import { activityShape } from 'Workspace/types/activity';
import { dispatch } from 'pluginServices/store';
import meShape from 'Workspace/types/me';
import { featureKeys } from 'services/FeatureFlagService';

import messages from './messages';
import actionMessages from "services/ExportService/messages";
import {
  Menu,
  MenuListItemText,
  Spacer,
  SubMenu,
  MenuListItemWrapper,
} from './styles';

import { pushToast, incrementDownloadErrorReportsCount, decrementDownloadErrorReportsCount, updateToast } from 'containers/ToastManager/actions';
import { publishFormat, status } from '../../../Publications/utils/constants';
import exportService from 'services/ExportService';
import enhanceWithFeatureFlagSubscription from 'hocs/enhanceWithFeatureFlagSubscription';
import { PLUGIN_TYPE_COURSE } from 'pluginServices/manifest/constants';
import { courseTypes, pluginList } from './constant';
import { pushPreflightToast } from '../../../../utils/preFlightToast';


const { enhanceWithPopper } = HOCs;
const MoreButtonWithPopper = enhanceWithPopper(BaseMoreButton);

const ProjectActivityListActions = ({ activity, currentUser, onAction, getFeatureFlagValue }) => {
  const [actionsList, setActionsList] = useState([]);
  const [duplicateSubActionsList, setDuplicateSubActionsList] = useState([]);
  const [checkReportSubActionsList, setCheckReportSubActionsList] = useState([]);
  const [isDuplicateSubMenuOpen, setIsDuplicateSubMenuOpen] = useState(false);
  const [isCheckReportSubMenuOpen, setIsCheckReportSubMenuOpen] = useState(false);
  const [activelyDownloadingReport, setActivelyDownloadingReport] = useState({activityId: null, outputType: null});
  const totalExportSnippets = useRef(0);
  const snippet = useRef(0);
  const {activityId} = activity;
  const {title} = activity.configFields;
  const isActivityDuplicateEnabled = getFeatureFlagValue(featureKeys.WORKSPACE_ACTIVITY_DUPLICATE);
  const isWorkspaceCourseExportReport = getFeatureFlagValue(featureKeys.WORKSPACE_COURSE_EXPORT_REPORT);
  const isDuplicateWithSubOptionsEnabled = getFeatureFlagValue(featureKeys.WORKSPACE_DUPLICATE_OPTIONS);
  const isPreflightPercentage = getFeatureFlagValue(featureKeys.BRNT_18784_WORKSPACE_ERROR_REPORT_PERCENTAGE_LOADER);
  
  const onCompleteDownloadReport = () => dispatch(decrementDownloadErrorReportsCount());
  const unsubscribeExportAndResetSnippets = (exportId) => {
    exportService.unSubscribeToExport(exportId);
    snippet.current = 0;
    totalExportSnippets.current = 0;
    setActivelyDownloadingReport({ activityId: null, outputType: null });
  };

  const requestPreflightReport = async (outputType) => {
    const { exportElementsCount, exportId } = await exportService.requestPreflightReport(activityId, outputType);
    if (exportId) {
      setActivelyDownloadingReport({activityId, outputType});
      pushPreflightToast(actionMessages.generatingReport, title, activityId, exportId, unsubscribeExportAndResetSnippets);
      totalExportSnippets.current = exportElementsCount;
      exportService.subscribeToExport(exportId, receiveOnExportComplete);
    }
  };
  
  const getPublicationReport = async (outputType) => {
    exportService.exportForReport(activityId, title, receiveOnExportComplete, outputType);
  };

  const receiveOnExportComplete = ({ response }) => {
    const { exportId, progress, ambrosiaUrl } = response;

    // Defensive guards
    const total = totalExportSnippets.current || 0;
    snippet.current = snippet.current + 1;

    // percentage math: avoid divide by zero and cap at 100
    const percentage = total > 0
      ? Math.min(100, Math.floor((snippet.current / total) * 100))
      : 0;

    if (isPreflightPercentage) {
      const toast = {
        toastId: activityId,
        percentage: percentage,
      };
      dispatch(updateToast(toast));
    }

    const isCompleted = status[progress] === status.COMPLETED;

    // If the server reports completed but we haven't yet received all the snippets
    if (isCompleted) {
      if (total > 0 && snippet.current < total) {
        if (isPreflightPercentage) {
          dispatch(updateToast({ toastId: activityId, percentage: 100 }));
        }
      }

      if (ambrosiaUrl) {
        unsubscribeExportAndResetSnippets(exportId);
        exportService.checkIfReportGenerated({ activityId, exportId, title }, onCompleteDownloadReport);
      }
    } else if (status[progress] === status.FAILED) {
      dispatch(decrementDownloadErrorReportsCount());
      unsubscribeExportAndResetSnippets(exportId);
      dispatch(pushToast({ message: intl.formatMessage(messages.reportFailed) }));
    }
  };

  const checkifReportDownloading = (type, outputType) => type === ActionTypes.CHECK_REPORT && activityId && activityId === activelyDownloadingReport?.activityId && outputType === activelyDownloadingReport?.outputType;
  const getCourseType = (plugin) => plugin.type === PLUGIN_TYPE_COURSE && pluginList[plugin.name] === courseTypes.ePub;
  const isPluginTypeEpub = getCourseType(activity.plugin);

  useEffect(() => {
    const _actionsList = [];
    const _duplicateSubActionsList = [];
    const _checkReportSubActionsList = [];

    if (isActivityDuplicateEnabled) {
      if (isDuplicateWithSubOptionsEnabled && isPluginTypeEpub) {
        setIsDuplicateSubMenuOpen(false);
        _duplicateSubActionsList.push({
          type: ActionTypes.DUPLICATE_EPUB,
          message: messages.ePub,
        });
        _duplicateSubActionsList.push({
          type: ActionTypes.DUPLICATE_BV,
          message: messages.bronteViewer,
        });
      }
      _actionsList.push({
        type: ActionTypes.DUPLICATE,
        icon: 'content_copy',
        message: messages.duplicate,
      });
    }
    if (activity.creator.accountId === currentUser.accountId) {
      _actionsList.push({
        type: ActionTypes.DELETE,
        icon: 'delete',
        message: messages.delete,
      });
    }
    if (isWorkspaceCourseExportReport) {
      setIsCheckReportSubMenuOpen(false);
      _checkReportSubActionsList.push({
        type: `${ActionTypes.CHECK_REPORT}-subAction1`,
        message: messages.pPlusInDesign,
      });
      _checkReportSubActionsList.push({
        type: `${ActionTypes.CHECK_REPORT}-subAction2`,
        message: messages.channelPartners,
        outputType: publishFormat.CHANNEL_PARTNER,
      });
      _actionsList.push({
        type: ActionTypes.CHECK_REPORT,
        icon: 'assignment_turned_in',
        message: messages.checkReport,
      });
    }
    setCheckReportSubActionsList(_checkReportSubActionsList);
    setDuplicateSubActionsList(_duplicateSubActionsList);
    setActionsList(_actionsList);
  }, [activity, currentUser, isActivityDuplicateEnabled, isWorkspaceCourseExportReport]);

  if (!actionsList.length) {
    return <Spacer />;
  }

  const handleMenuClick = (type) => {
    if (type === ActionTypes.DUPLICATE && isDuplicateWithSubOptionsEnabled && isPluginTypeEpub) {
      setIsDuplicateSubMenuOpen(!isDuplicateSubMenuOpen);
      setIsCheckReportSubMenuOpen(false);
    } else if (type === ActionTypes.CHECK_REPORT) {
      setIsCheckReportSubMenuOpen(!isCheckReportSubMenuOpen);
      setIsDuplicateSubMenuOpen(false);
    } else {
      onClickItem(type)();
    }
  };

  const onClickItem = (type, outputType) => () => {
    if (type === ActionTypes.CHECK_REPORT) {
      dispatch(incrementDownloadErrorReportsCount());
      if (isPreflightPercentage) {
        requestPreflightReport(outputType);
      } else {
        getPublicationReport(outputType);
      }
    }
    setIsDuplicateSubMenuOpen(false);
    setIsCheckReportSubMenuOpen(false);
    onAction(type, activity);

  };

  return (
    <MoreButtonWithPopper
      placement="bottom-end"
      hidepopperonclick={actionsList.every(
        (item) => item.type !== ActionTypes.DUPLICATE && isDuplicateWithSubOptionsEnabled
      )}
    >
      <Menu>
        <MenuList>
          {actionsList.map((item) => (
            <MenuListItem
              key={item.type}
              onClick={() => handleMenuClick(item.type)}
              data-type={`${item.type}`}
            >
              <MaterialIcon id={item.icon} />
              <MenuListItemText>
                {intl.formatMessage(item.message)}
              </MenuListItemText>
              {item.type === ActionTypes.DUPLICATE && isDuplicateWithSubOptionsEnabled && isPluginTypeEpub && duplicateSubActionsList.length > 0 && (
                <>
                  <MaterialIcon id={"chevron_right"} />
                  {isDuplicateSubMenuOpen && (
                    <SubMenu>
                      <MenuList>
                        {duplicateSubActionsList.map((subItem) => (
                          <MenuListItem
                            key={subItem?.message?.id}
                            onClick={onClickItem(subItem.type)}
                          >
                            <MenuListItemText>{intl.formatMessage(subItem?.message)}</MenuListItemText>
                          </MenuListItem>
                        ))}
                      </MenuList>
                    </SubMenu>
                  )}
                </>
              )}
              {item.type === ActionTypes.CHECK_REPORT && checkReportSubActionsList.length > 0 && (
                <>
                  <MaterialIcon id={"chevron_right"} />
                  {isCheckReportSubMenuOpen && (
                    <SubMenu>
                      <MenuList>
                        {checkReportSubActionsList.map((subItem) => (
                          <MenuListItemWrapper key={subItem.type} activelyDownloadingReport={checkifReportDownloading(item.type, subItem.outputType)}>
                            <MenuListItem
                              key={subItem?.message?.id}
                              disabled={checkifReportDownloading(item.type, subItem.outputType)}
                              onClick={onClickItem(item.type, subItem.outputType)}
                              data-type={subItem?.type}
                            >
                              <MenuListItemText>{intl.formatMessage(subItem?.message)}</MenuListItemText>
                            </MenuListItem>
                          </MenuListItemWrapper>
                        ))}
                      </MenuList>
                    </SubMenu>
                  )}
                </>
              )}
            </MenuListItem>
          ))}
        </MenuList>
      </Menu>
    </MoreButtonWithPopper>
  );
};

ProjectActivityListActions.propTypes = {
  activity: activityShape,
  onAction: PropTypes.func,
  currentUser: meShape,
};

const flagNames = [featureKeys.WORKSPACE_ACTIVITY_DUPLICATE, featureKeys.WORKSPACE_COURSE_EXPORT_REPORT, featureKeys.BRNT_18784_WORKSPACE_ERROR_REPORT_PERCENTAGE_LOADER];
export default enhanceWithFeatureFlagSubscription(ProjectActivityListActions, flagNames);
